import React, {
    FunctionComponent,
    ReactElement,
    useCallback,
    useMemo,
    useState,
} from "react";

import { styled } from "@mui/material";

import { Info } from "@mui/icons-material";

import { useGetAssetById } from "../../sdk/hooks/assets/useGetAssetById";

import { AssetDetailsDialog } from "./AssetDetailsDialog";
import { ProductVideo } from "./ProductVideo";

const GridContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(4)};

    width: 100%;

    padding: ${({ theme }) => theme.spacing(2)};

    background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
    border-radius: ${({ theme }) => theme.spacing(2)};

    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const TagSection = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const TagHeader = styled("div")`
    font-size: 18px;
    font-weight: 600;
    color: #333333;

    padding-left: ${({ theme }) => theme.spacing(2)};
    border-left: 4px solid #000000;
`;

const ImageGrid = styled("div")`
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(2)};

    padding: ${({ theme }) => theme.spacing(2)};
`;

const ImageContainer = styled("div")`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};

    min-width: 220px;

    padding: ${({ theme }) => theme.spacing(1)};

    background: #ffffff;
    border-radius: ${({ theme }) => theme.spacing(1.5)};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    }
`;

const VideoContainer = styled("div")`
    position: relative;
    width: 200px;
    height: 200px;
    border-radius: ${({ theme }) => theme.spacing(1)};
    overflow: hidden;
`;

const InfoButton = styled("button")`
    position: absolute;
    top: ${({ theme }) => theme.spacing(1)};
    right: ${({ theme }) => theme.spacing(1)};

    display: flex;
    align-items: center;
    justify-content: center;

    width: 32px;
    height: 32px;

    background-color: rgba(0, 0, 0, 0.7);
    border: none;
    border-radius: 50%;

    color: white;
    cursor: pointer;

    transition: background-color 0.3s ease, transform 0.2s ease;

    &:hover {
        background-color: rgba(0, 0, 0, 0.9);
        transform: scale(1.1);
    }

    &:active {
        transform: scale(0.95);
    }
`;

const VariantImage = styled("img")`
    height: 200px;
    width: auto;
    max-width: 100%;

    border-radius: ${({ theme }) => theme.spacing(1)};

    cursor: pointer;
    object-fit: cover;
`;

const PositionText = styled("div")`
    font-size: 13px;
    font-weight: 500;
    color: #555555;

    text-align: center;
`;

const ToggleButton = styled("button")`
    align-self: center;
    padding: ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.spacing(2)};
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;

    background-color: #000000;
    border: none;
    border-radius: ${({ theme }) => theme.spacing(1)};
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: #555555;
    }
`;

export interface IImageCarouselProps {
    imageUrls: { objectUrl: string; position?: number; tags?: string }[];
    assetIds: string[];
}

export const ImageCarousel: FunctionComponent<IImageCarouselProps> = (
    props: IImageCarouselProps,
): ReactElement => {
    const { imageUrls, assetIds } = props;
    const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
    const [showAll, setShowAll] = useState<boolean>(false);
    const { data: assetDetails } = useGetAssetById(
        "assets",
        selectedAssetId ?? "",
        { enabled: !!selectedAssetId },
    );

    /*
 Groups images by their tags using useMemo for memoization.
 - Defaults tag to "untagged" if not present.
 - Each group includes the image data along with its corresponding assetId.
 - Images within each tag group are sorted by their position.
*/
    const groupedImages = useMemo(() => {
        const groupedByTag = imageUrls.reduce(
            (accumulatedGroups, currentImage, currentIndex) => {
                const tag = currentImage.tags ?? "untagged";

                if (!accumulatedGroups[tag]) {
                    accumulatedGroups[tag] = [];
                }

                accumulatedGroups[tag].push({
                    ...currentImage,
                    assetId: assetIds[currentIndex],
                });

                return accumulatedGroups;
            },
            {} as Record<
                string,
                {
                    objectUrl: string;
                    position?: number;
                    tags?: string;
                    assetId: string;
                }[]
            >,
        );

        for (const tag of Object.keys(groupedByTag)) {
            groupedByTag[tag].sort(
                (firstImage, secondImage) =>
                    (firstImage.position ?? 0) - (secondImage.position ?? 0),
            );
        }

        return groupedByTag;
    }, [imageUrls, assetIds]);

    /*
 Creates a sorted list of tag names using useMemo.
 - "untagged" group is pushed to the end.
 - If `showAll` is false, only the first group is returned.
*/
    const sortedTags = useMemo(() => {
        return Object.keys(groupedImages)
            .sort((firstTag, secondTag) =>
                firstTag === "untagged"
                    ? 1
                    : secondTag === "untagged"
                    ? -1
                    : firstTag.localeCompare(secondTag),
            )
            .slice(0, showAll ? undefined : 1);
    }, [groupedImages, showAll]);

    const handleCloseAsset = useCallback(() => {
        setSelectedAssetId(null);
    }, []);

    const handleVideoInfoClick = useCallback((assetId: string) => {
        setSelectedAssetId(assetId);
    }, []);

    return (
        <GridContainer>
            {sortedTags.map((tag) => (
                <TagSection key={tag}>
                    <TagHeader>{tag}</TagHeader>
                    <ImageGrid>
                        {groupedImages[tag].map((item, index) => {
                            const checkVideo = /\.(mp4|mov|webm)$/i.test(
                                item.objectUrl ?? "",
                            );
                            return (
                                <ImageContainer key={index}>
                                    {checkVideo && (
                                        <VideoContainer>
                                            <ProductVideo
                                                source={item.objectUrl ?? ""}
                                                autoPlay={false}
                                                muted={false}
                                                width="200px"
                                                borderRadius="8px"
                                                stopPlaying={false}
                                            />
                                            <InfoButton
                                                onClick={() =>
                                                    handleVideoInfoClick(
                                                        item.assetId,
                                                    )
                                                }
                                                title="View asset details">
                                                <Info fontSize="small" />
                                            </InfoButton>
                                        </VideoContainer>
                                    )}
                                    {!checkVideo && (
                                        <VariantImage
                                            src={item.objectUrl}
                                            alt={`Variant ${tag} ${index}`}
                                            onClick={() =>
                                                setSelectedAssetId(item.assetId)
                                            }
                                        />
                                    )}
                                    <PositionText>
                                        Position: {item.position ?? "N/A"}
                                    </PositionText>
                                </ImageContainer>
                            );
                        })}
                    </ImageGrid>
                </TagSection>
            ))}
            {Object.keys(groupedImages).length > 1 && (
                <ToggleButton onClick={() => setShowAll(!showAll)}>
                    {showAll ? "Show Less" : "Show More"}
                </ToggleButton>
            )}
            <AssetDetailsDialog
                open={!!selectedAssetId}
                onClose={handleCloseAsset}
                assetDetails={assetDetails}
                url={
                    selectedAssetId
                        ? imageUrls[assetIds.indexOf(selectedAssetId)]
                              ?.objectUrl
                        : ""
                }
                assetId={selectedAssetId}
            />
        </GridContainer>
    );
};
